import { formatPaymentError } from "./formatPaymentError";
import { devInfo } from "../utils/devLog";

/**
 * Hosted-checkout CIT payment outcomes (industry-standard):
 * - Decline: HTTP 200, stay on page, inline error, retry allowed
 * - Redirect: HTTP 200, leave to gateway (3DS, PIX, UPI, APM, open banking)
 * - Success: confirm + optional merchant redirect
 */

export const CIT_OUTCOME = {
  DECLINED: "declined",
  REDIRECT: "redirect",
  SUCCESS: "success",
  PENDING: "pending",
  ERROR: "error",
};

export function isCitPaymentDeclined(res) {
  return res?.status === 200 && res?.data?.status === "failed";
}

/** Merchant-facing detail (logged only — never shown to shoppers on checkout). */
export function getMerchantCitFailureDetail(parsed, res) {
  const data = parsed?.data || res?.data || {};
  return {
    outcome: parsed?.outcome,
    message: parsed?.message,
    failureReason: data.failureReason,
    failureReasonCode: data.failureReasonCode,
    failureReasonLabel: data.failureReasonLabel,
    gatewayStatusCode: data.gatewayStatusCode,
    responseCode: data.responseCode,
    transactionId: data.transactionId || data.newTransactionId,
  };
}

/**
 * Generic copy for declined/failed CIT payments — avoids exposing gateway codes to shoppers.
 */
export function getShopperCitFailureMessage(t) {
  return (
    t?.("messages.error.paymentDeclinedRetry") ||
    "We couldn't process the payment. Please enter new payment details and try again."
  );
}

export function getCitDeclineMessage(res, t) {
  const raw = res?.data?.failureReason || res?.data?.message;
  return formatPaymentError(raw, t);
}

const isSessionExpiredMessage = (detail, t) => {
  if (!detail) return false;
  const sessionMsg =
    t?.("messages.error.sessionExpired") || "Session expired. Please try again.";
  return detail === sessionMsg || String(detail).toLowerCase().includes("session expired");
};

export function resolveCitRedirectUrl(data) {
  if (!data) return null;
  const url = data.redirect_url || data.redirectUrl;
  if (!url || typeof url !== "string") return null;
  return url;
}

/**
 * Parses a process-payment API response into a normalized outcome.
 */
export function parseCitPaymentResponse(res, t) {
  if (!res || res.status !== 200) {
    return {
      outcome: CIT_OUTCOME.ERROR,
      data: res?.data,
      message: extractErrorMessage(res, t),
    };
  }

  const data = res.data || {};

  if (data.status === "failed") {
    return {
      outcome: CIT_OUTCOME.DECLINED,
      data,
      message: getCitDeclineMessage(res, t),
    };
  }

  const redirectUrl = resolveCitRedirectUrl(data);
  if (redirectUrl) {
    return {
      outcome: CIT_OUTCOME.REDIRECT,
      data,
      redirectUrl,
      transactionId: data.newTransactionId || data.transactionId,
    };
  }

  if (data.status === "pending") {
    return {
      outcome: CIT_OUTCOME.PENDING,
      data,
      message: data.message,
      transactionId: data.newTransactionId || data.transactionId,
    };
  }

  return {
    outcome: CIT_OUTCOME.SUCCESS,
    data,
    transactionId: data.newTransactionId || data.transactionId,
    hostedRedirect: data.hosted_redirect_page,
  };
}

function extractErrorMessage(res, t) {
  const data = res?.data;
  if (!data) return null;
  if (typeof data === "string") return formatPaymentError(data, t);
  const raw = data.status === "failed" && data.message ? data.message : data.message || data.error?.message;
  return raw ? formatPaymentError(raw, t) : null;
}

/**
 * Applies UX for decline / error on hosted vs classic checkout.
 * Returns true when the outcome was handled (caller should stop).
 */
export function applyCitDeclineOrErrorUi(parsed, options = {}) {
  const {
    isHostedPageMode = false,
    isEmbeddedCheckout = false,
    t,
    setError,
    setTransactionStatus,
    setShowStatusModal,
    onParentCallback,
    toast,
  } = options;

  const merchantDetail = getMerchantCitFailureDetail(parsed);
  if (process.env.NODE_ENV === "development" && merchantDetail.message) {
    devInfo("[CIT payment failure — merchant detail]", merchantDetail);
  }

  const sessionMsg =
    t?.("messages.error.sessionExpired") || "Session expired. Please try again.";
  if (isSessionExpiredMessage(parsed.message, t) && toast && !isEmbeddedCheckout) {
    toast.error(sessionMsg);
    return true;
  }

  const message = parsed?.message || getShopperCitFailureMessage(t);

  if (setError) setError(message);

  if (isEmbeddedCheckout && onParentCallback) {
    onParentCallback({ type: "failure", message });
    return true;
  }

  if (!isHostedPageMode) {
    if (setTransactionStatus) setTransactionStatus("fail");
    if (setShowStatusModal) setShowStatusModal(true);
  }

  return true;
}

/**
 * Main entry: parse response and apply standard hosted-page behaviour.
 *
 * @returns {{ handled: boolean, parsed: object }}
 */
/**
 * Records a client-side gateway SDK decline and applies standard CIT decline UX.
 * Use when the gateway charges/authorizes in the browser before process-payment (Nuvei).
 *
 * @returns {Promise<{ recorded: boolean, handled: boolean }>}
 */
export async function recordAndHandleCitGatewayDecline(
  recordDeclineApi,
  billingPlatformId,
  sessionId,
  { checkoutRequest, failureMessage, statusDetail, statusCode, idAtPaymentGateway, gatewayResponse },
  citUiOptions = {}
) {
  try {
    const res = await recordDeclineApi(billingPlatformId, sessionId, {
      checkoutRequest,
      failureMessage,
      statusDetail,
      statusCode,
      idAtPaymentGateway,
      gatewayResponse,
    });
    const { handled } = handleCitPaymentApiResponse(res, citUiOptions);
    return { recorded: true, handled };
  } catch (err) {
    console.warn("Failed to record gateway SDK decline:", err);
    if (err?.response && handleCitPaymentApiResponse(err.response, citUiOptions).handled) {
      return { recorded: false, handled: true };
    }
    return { recorded: false, handled: false };
  }
}

export function handleCitPaymentApiResponse(res, options = {}) {
  const parsed = parseCitPaymentResponse(res, options.t);

  if (parsed.outcome === CIT_OUTCOME.DECLINED || parsed.outcome === CIT_OUTCOME.ERROR) {
    applyCitDeclineOrErrorUi(
      {
        ...parsed,
        message:
          parsed.message ||
          (parsed.outcome === CIT_OUTCOME.ERROR
            ? options.t?.("messages.error.paymentFailed")
            : getCitDeclineMessage(res, options.t)),
      },
      options
    );
    return { handled: true, parsed };
  }

  return { handled: false, parsed };
}
